/**
 * Timing of the elevator (seconds, meters). The numbers are those of a modern game lift: doors
 * take 1.2 s, wait 3 s, and a one-floor ride takes about 1.6 s with constant acceleration up to
 * a cruise speed and the same braking (an ease-in and ease-out of the position).
 */
export const TIMING = {
  /** Doors opening or closing. */
  door: 1.2,
  /** Doors stay open this long after the last call or the last person passing. */
  dwell: 3,
  /** After a floor is pressed inside while the doors are open, they close after this. */
  grace: 1.0,
  /** Acceleration limit (m/s^2): a 4 m floor in 1.6 s (0.64 g at the peak). */
  accel: 6.25,
  /** Cruise speed (m/s). */
  speed: 8,
} as const;

/** Shortened by the "reduce motion" setting (and by tests that do not look at the ride). */
export const QUICK = { door: 0.3, dwell: 0.8, grace: 0.2, accel: 40, speed: 30 } as const;
export type Timing = { [K in keyof typeof TIMING]: number };

/** Smooth 0..1 ease for door leaves (zero speed at both ends). */
export const easeDoor = (x: number): number => {
  const k = x < 0 ? 0 : x > 1 ? 1 : x;
  return k * k * (3 - 2 * k);
};

/** The time to ride `distance` meters from rest to rest. */
export function travelTime(distance: number, tm: Timing = TIMING): number {
  const d = Math.abs(distance);
  if (d <= 1e-9) return 0;
  const ramp = (tm.speed * tm.speed) / tm.accel; // distance to reach the cruise speed and stop again
  if (d <= ramp) return 2 * Math.sqrt(d / tm.accel);
  return (2 * tm.speed) / tm.accel + (d - ramp) / tm.speed;
}

/** Distance covered (0..distance, unsigned) `t` seconds after leaving. */
export function travelAt(distance: number, t: number, tm: Timing = TIMING): number {
  const d = Math.abs(distance);
  const total = travelTime(d, tm);
  if (t <= 0) return 0;
  if (t >= total) return d;
  const a = tm.accel;
  const ramp = (tm.speed * tm.speed) / a;
  const peak = d <= ramp ? Math.sqrt(d * a) : tm.speed;
  const ta = peak / a; // time of the acceleration (and of the braking)
  if (t < ta) return 0.5 * a * t * t;
  const dAcc = 0.5 * a * ta * ta;
  const cruise = total - 2 * ta;
  if (t < ta + cruise) return dAcc + peak * (t - ta);
  const r = total - t;
  return d - 0.5 * a * r * r;
}

/** Speed (m/s, unsigned) at `t`. */
export function speedAt(distance: number, t: number, tm: Timing = TIMING): number {
  const d = Math.abs(distance);
  const total = travelTime(d, tm);
  if (t <= 0 || t >= total) return 0;
  const a = tm.accel;
  const ramp = (tm.speed * tm.speed) / a;
  const peak = d <= ramp ? Math.sqrt(d * a) : tm.speed;
  const ta = peak / a;
  if (t < ta) return a * t;
  if (t < total - ta) return peak;
  return a * (total - t);
}

/**
 * The floor (index into `ys`) whose interior the player sees at height `y`: the nearest floor
 * surface, switching only once `y` is past the midpoint to another one (`from` is the floor drawn
 * now, so a rider hovering on the midpoint does not flicker).
 */
export function floorAtHeight(ys: readonly number[], y: number, from: number): number {
  let i = Math.max(0, Math.min(ys.length - 1, from));
  while (i + 1 < ys.length && y > (ys[i]! + ys[i + 1]!) / 2) i++;
  while (i > 0 && y < (ys[i]! + ys[i - 1]!) / 2) i--;
  return i;
}
